import { useState } from "react";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  getManualRowAudit,
  getManualRowDisplay,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { useDeleteManualRow } from "@/payroll/ai-script/query";
import {
  type IAiSpreadsheetCellLocation,
  type IAiSpreadsheetManualRow,
  type IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import {
  BoldValue,
  CellLocationLink,
  EditCard,
  showPersistentEditMutationError,
} from "./shared";

interface ManualRowRowProps {
  row: IAiSpreadsheetManualRow;
  businessId: string | null;
  runId: string;
  sheets:
    | Pick<
        IAiSpreadsheetSheet,
        "id" | "name" | "recipient_external_id" | "editable"
      >[]
    | undefined;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  // Fired on a successful remove (advances the server workbook version) so the
  // session undo/redo stack can be reset.
  onWorkbookMutated?: () => void;
}

// One added/manual row in the Manual edits panel. Unlike a cell override there is
// no "needs review" state — a user-added row is always applied — so the only
// action is to remove it (reverting the sheet to pure script output for that row).
export const ManualRowRow = ({
  row,
  businessId,
  runId,
  sheets,
  onGoToCell,
  onWorkbookMutated,
}: ManualRowRowProps) => {
  const del = useDeleteManualRow(businessId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The row carries a sheet name/recipient, not an id; resolve the live sheet so
  // the delete invalidates the right cache and respects edit access.
  const sheet = sheets?.find(
    (candidate) =>
      candidate.name === row.sheet_name &&
      candidate.recipient_external_id === row.recipient_external_id,
  );
  const canDelete = !!sheet?.editable;
  const display = getManualRowDisplay(row);
  const audit = getManualRowAudit(row);

  const onDelete = () => {
    if (!sheet) return;
    del.mutate(
      { runId, sheetId: sheet.id, manualRowId: row.id },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          onWorkbookMutated?.();
        },
        onError: (error) =>
          showPersistentEditMutationError("Couldn't remove the row", error),
      },
    );
  };

  return (
    <EditCard>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap="sm"
        fullWidth
        className="min-w-0"
      >
        {row.cell ? (
          <CellLocationLink cell={row.cell} onGoToCell={onGoToCell} />
        ) : (
          <Typography
            variant="subtitleW3"
            textColor="tertiary"
            className="truncate"
          >
            {row.sheet_name}
          </Typography>
        )}
        {display.wasMoved ? (
          <Badge variant="gold" size="sm" className="shrink-0">
            Moved
          </Badge>
        ) : (
          <Badge variant="blue" size="sm" className="shrink-0">
            Added row
          </Badge>
        )}
      </Stack>

      <Typography variant="bodyW1" className="min-w-0 truncate">
        <BoldValue>{display.label}</BoldValue>
      </Typography>

      {display.note ? (
        <Typography
          variant="bodyW2"
          textColor="secondary"
          className="break-words"
        >
          {display.note}
        </Typography>
      ) : null}

      {/* Same attribution line a cell override carries, so an added row is as
          accountable as an edited one. Two lines when the row was edited after it
          was added — that edit has no override entry of its own to show it. */}
      {audit.map((line) => (
        <Typography
          key={line}
          variant="captionW1"
          textColor="tertiary"
          className="truncate"
        >
          {line}
        </Typography>
      ))}

      {/* Inline confirmation — removing a row deletes it from the sheet and can't
          be undone from here, so make the consequence explicit before removing. */}
      {confirmOpen ? (
        <InlineConfirmation
          message={
            <>
              Remove this row? It&apos;s deleted from the sheet and can&apos;t
              be recovered — you&apos;d have to add it back manually.
            </>
          }
          confirmLabel="Remove row"
          isPending={del.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onDelete}
        />
      ) : canDelete ? (
        <Stack direction="row" justify="end" fullWidth className="pt-1">
          <Button
            variant="tertiary"
            size="xs"
            onClick={() => setConfirmOpen(true)}
          >
            Remove
          </Button>
        </Stack>
      ) : null}
    </EditCard>
  );
};
